import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MessageCircle, Shield, Scale, Award, AlertCircle, Heart, ChevronRight, BookOpen, ArrowRight, Clock, Calculator, FileText } from 'lucide-react';
import SEOHead from '@/components/SEOHead';
import RelatedPracticeAreas from '@/components/RelatedPracticeAreas';
import PracticeAreaFAQ from '@/components/PracticeAreaFAQ';
import PageVideoSection from '@/components/PageVideoSection';

// Motorcycle-specific video
const motorcycleVideos = [
  {
    id: 'iiOUemP3Udw',
    title: "Motorcycle Safety Tips",
    description: "Quick tips for staying safe on your bike this riding season.",
    duration: "0:36"
  }
];

const motorcycleFAQs = [
  {
    question: "Why do motorcycle accident claims face more challenges than car accident claims?",
    answer: "Insurance companies often harbor bias against motorcyclists, assuming they're reckless or at fault. I understand rider culture and fight back against these stereotypes, ensuring you're treated fairly and your claim isn't unfairly reduced."
  },
  {
    question: "What if the driver says they didn't see me?",
    answer: "'I didn't see the motorcycle' is not a valid defense. Drivers have a duty to look carefully before changing lanes or turning. This excuse actually demonstrates negligence, and I use it to strengthen your case, not weaken it."
  },
  {
    question: "Should I accept the insurance company's first offer?",
    answer: "Almost never. First offers are typically far below what your case is worth. Insurance companies know motorcycle injuries are often severe, and they try to settle quickly before you understand the full extent of your injuries and damages."
  },
  {
    question: "What compensation can I recover after a motorcycle accident?",
    answer: "You may be entitled to medical expenses, lost wages, pain and suffering, property damage (including your bike), future medical care, and diminished earning capacity. Motorcycle injuries are often serious, so these amounts can be substantial."
  },
  {
    question: "How long do I have to file a motorcycle accident claim in Washington?",
    answer: "You have 3 years from the accident date under Washington's statute of limitations (RCW 4.16.080). However, evidence and witness memories fade quickly—contact me as soon as possible to protect your claim."
  },
  {
    question: "What if I wasn't wearing a helmet?",
    answer: "Washington requires helmets, but not wearing one doesn't automatically bar your claim. While it may reduce damages if it contributed to head injuries, you can still recover for other injuries. Each case is evaluated individually."
  }
];

const MotorcyclePage = () => {
  // HowTo schema for "What to Do After a Motorcycle Accident"
  const howToSteps = {
    name: "What to Do After a Motorcycle Accident in Washington State",
    description: "Critical steps for motorcyclists to protect their health, rights, and injury claim after a crash.",
    totalTime: "PT45M",
    steps: [
      { name: "Check for Injuries and Get to Safety", text: "Assess yourself for injuries. If possible, move yourself and your motorcycle out of traffic to a safe location. Call 911 immediately." },
      { name: "Seek Medical Attention", text: "Get medical care even if injuries seem minor. Motorcycle accidents often cause delayed symptoms like internal bleeding or spinal injuries." },
      { name: "Document the Accident Scene", text: "Take photos of your motorcycle damage, the other vehicle, road conditions, traffic signs, skid marks, and any visible injuries before anything is moved." },
      { name: "Collect Driver and Witness Information", text: "Get the other driver's license, insurance, and contact information. Collect names and phone numbers of any witnesses." },
      { name: "File a Police Report", text: "Request officers document the accident. Get the report number. The police report is crucial evidence for your claim." },
      { name: "Don't Admit Fault or Apologize", text: "Avoid saying 'I'm sorry' or admitting any fault. Stick to the facts when talking to police, witnesses, or the other driver." },
      { name: "Preserve Your Gear and Motorcycle", text: "Keep your helmet, jacket, gloves, and motorcycle as evidence of the impact. Do not repair or dispose of damaged equipment." },
      { name: "Contact a Motorcycle Accident Attorney", text: "Speak with an attorney before talking to any insurance company. Insurance adjusters often have bias against motorcyclists." }
    ]
  };

  return (
    <div className="min-h-screen pt-20" data-testid="motorcycle-page">
      <SEOHead 
        title="Motorcycle Accident Attorney | Washington State | Amy C. Brown Law"
        description="Dedicated motorcycle accident lawyer understanding rider culture and the unique challenges motorcyclists face. Fighting for fair compensation against insurance bias."
        serviceData={{
          name: "Motorcycle Accident Legal Representation",
          description: "Specialized legal representation for motorcycle accident victims in Washington State, fighting insurance bias and securing fair compensation for serious injuries.",
          category: "Personal Injury Law"
        }}
        howToData={howToSteps}
      />
      {/* Hero Section */}
      <section className="pt-36 pb-16 md:pt-40 md:pb-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-3xl">
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-secondary mb-6">
              Motorcycle Crashes
            </h1>
            <p className="text-xl text-slate-600 mb-8 leading-relaxed">
              Motorcycle accidents can result in severe injuries due to the lack of protection compared to cars. As a dedicated personal injury attorney, I understand the unique challenges motorcyclists face after an accident. I fight to ensure you receive maximum compensation for your recovery.
            </p>
            <div className="flex flex-wrap gap-4">
              <a 
                href="tel:425-675-1224"
                className="inline-flex items-center gap-2 bg-primary text-white px-8 py-4 rounded-full font-semibold hover:bg-primary/90 transition-all duration-300"
                data-testid="motorcycle-hero-call"
              >
                <Phone className="w-5 h-5" />
                Call (425) 675-1224
              </a>
              <a 
                href="sms:425-675-1224"
                className="inline-flex items-center gap-2 border-2 border-secondary text-secondary px-8 py-4 rounded-full font-semibold hover:bg-secondary hover:text-white transition-all duration-300"
              >
                <MessageCircle className="w-5 h-5" />
                Text Me
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Understanding Motorcycle Injuries */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mb-6">
                Motorcycle Accident Injuries Require Experienced Representation
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                Motorcycle riders face unique risks on the road. When accidents happen, the injuries are often more severe than those in car accidents—broken bones, road rash, traumatic brain injuries, and spinal cord damage are common. Insurance companies often try to blame motorcyclists or minimize the severity of injuries.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                With over ten years of experience, including time at a defense firm, I know how insurance companies operate and how to counter their tactics. I fight aggressively to ensure motorcyclists receive fair compensation for:
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-teal-600 text-sm">✓</span>
                  </span>
                  <span className="text-slate-700">Medical expenses and ongoing care</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-teal-600 text-sm">✓</span>
                  </span>
                  <span className="text-slate-700">Lost wages and loss of earning capacity</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-teal-600 text-sm">✓</span>
                  </span>
                  <span className="text-slate-700">Pain and suffering</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-teal-600 text-sm">✓</span>
                  </span>
                  <span className="text-slate-700">Motorcycle repair or replacement</span>
                </li>
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div className="bg-slate-50 rounded-2xl p-6 text-center hover-lift">
                <Shield className="w-10 h-10 text-teal-600 mx-auto mb-4" />
                <h4 className="font-semibold text-secondary mb-2">Insurance Insight</h4>
                <p className="text-sm text-slate-600">Former defense attorney—I know their tactics against motorcyclists</p>
              </div>
              <div className="bg-slate-50 rounded-2xl p-6 text-center hover-lift">
                <Scale className="w-10 h-10 text-teal-600 mx-auto mb-4" />
                <h4 className="font-semibold text-secondary mb-2">No Fees Unless You Win</h4>
                <p className="text-sm text-slate-600 mb-2">Contingency fee basis means no upfront costs</p>
                <Link to="/pricing" className="inline-flex items-center gap-1 text-teal-600 text-sm font-medium hover:text-teal-700">
                  See pricing <ChevronRight className="w-3 h-3" />
                </Link>
              </div>
              <div className="bg-slate-50 rounded-2xl p-6 text-center hover-lift">
                <Award className="w-10 h-10 text-teal-600 mx-auto mb-4" />
                <h4 className="font-semibold text-secondary mb-2">10+ Years Experience</h4>
                <p className="text-sm text-slate-600">Proven track record with injury cases</p>
              </div>
              <div className="bg-slate-50 rounded-2xl p-6 text-center hover-lift">
                <Heart className="w-10 h-10 text-teal-600 mx-auto mb-4" />
                <h4 className="font-semibold text-secondary mb-2">Personal Attention</h4>
                <p className="text-sm text-slate-600">Work directly with me throughout your case</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Common Causes */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary text-center mb-12">
            Common Causes of Motorcycle Accidents
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              'Drivers failing to see motorcycles',
              'Left-turn accidents at intersections',
              'Distracted driving',
              'Speeding and reckless driving',
              'Drunk or impaired drivers',
              'Road hazards and poor conditions',
              'Lane splitting accidents',
              'Rear-end collisions',
              'Dooring accidents'
            ].map((cause, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-md flex items-center gap-4">
                <span className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-teal-600 text-sm font-bold">{index + 1}</span>
                </span>
                <span className="text-slate-700">{cause}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Personal Touch */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <div className="bg-accent-soft rounded-2xl p-10 border-l-4 border-teal-500">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-secondary mb-6">
              I Understand What You're Going Through
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed mb-6">
              At Amy C Brown Law, I'm more than an attorney—I've experienced trauma myself. Multiple members of my family were killed in a construction-related incident. I vividly remember that horrible day and the days after. I know pain and I understand the pain my clients feel.
            </p>
            <p className="text-lg text-slate-600 leading-relaxed">
              Maybe you've never dealt with lawyers before, and trusting one feels daunting. I've been a lawyer for over ten years and have guided countless people through the chaos you may be going through. I'll listen, explain your options clearly, and handle the complexities so you can heal.
            </p>
          </div>
        </div>
      </section>

      {/* Statute of Limitations */}
      <section className="py-20 bg-secondary text-white">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center">
          <AlertCircle className="w-16 h-16 text-teal-400 mx-auto mb-6" />
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
            Take Timely Action to Protect Your Case
          </h2>
          <p className="text-xl text-slate-300 leading-relaxed mb-8">
            Do not let precious time slip away—acting swiftly is critical to protect your case. Evidence can vanish, and memories of witnesses may fade, jeopardizing your opportunity for a positive resolution. With a three-year statute of limitations for most personal injury claims in Washington, I urge you to contact me today.
          </p>
        </div>
      </section>

      {/* Helpful Resources - Internal Linking */}
      <section className="py-16 bg-slate-50" data-testid="motorcycle-resources-section">
        <div className="container mx-auto px-4 md:px-6 max-w-5xl">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <BookOpen className="w-4 h-4" />
              Learn More
            </div>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-secondary mb-3">
              Resources for Motorcycle Riders
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Motorcycle accidents often result in serious injuries. Know your rights and options.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            <Link
              to="/blog/what-to-do-after-car-accident-washington"
              className="group bg-white hover:bg-teal-50 rounded-xl p-6 border border-slate-200 hover:border-teal-300 transition-all duration-300 shadow-sm"
            >
              <FileText className="w-10 h-10 text-teal-600 mb-4" />
              <h3 className="font-heading font-bold text-secondary mb-2 group-hover:text-teal-700 transition-colors">
                What to Do After an Accident
              </h3>
              <p className="text-slate-600 text-sm mb-3">
                Critical steps to protect your rights and build a strong case from the scene.
              </p>
              <span className="inline-flex items-center gap-1 text-teal-600 font-medium text-sm group-hover:gap-2 transition-all">
                Read Guide <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
            
            <Link
              to="/blog/why-statutes-of-limitations-exist-washington-personal-injury"
              className="group bg-white hover:bg-teal-50 rounded-xl p-6 border border-slate-200 hover:border-teal-300 transition-all duration-300 shadow-sm"
            >
              <Clock className="w-10 h-10 text-teal-600 mb-4" />
              <h3 className="font-heading font-bold text-secondary mb-2 group-hover:text-teal-700 transition-colors">
                Filing Deadlines in Washington
              </h3>
              <p className="text-slate-600 text-sm mb-3">
                Don't miss the 3-year deadline—learn about exceptions and why timing matters.
              </p>
              <span className="inline-flex items-center gap-1 text-teal-600 font-medium text-sm group-hover:gap-2 transition-all">
                Learn More <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
            
            <Link
              to="/compensation-estimator"
              className="group bg-white hover:bg-teal-50 rounded-xl p-6 border border-slate-200 hover:border-teal-300 transition-all duration-300 shadow-sm"
            >
              <Calculator className="w-10 h-10 text-teal-600 mb-4" />
              <h3 className="font-heading font-bold text-secondary mb-2 group-hover:text-teal-700 transition-colors">
                Estimate Your Compensation
              </h3>
              <p className="text-slate-600 text-sm mb-3">
                Use our free calculator to get an idea of what your case may be worth.
              </p>
              <span className="inline-flex items-center gap-1 text-teal-600 font-medium text-sm group-hover:gap-2 transition-all">
                Try Calculator <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <PracticeAreaFAQ
        title="Motorcycle Accident FAQs"
        subtitle="Common questions about motorcycle crash claims in Washington State."
        faqs={motorcycleFAQs}
        accentColor="primary"
        schemaId="motorcycle"
      />

      {/* Video Resources Section */}
      <PageVideoSection
        videos={motorcycleVideos}
        title="Motorcycle Safety"
        subtitle="Tips for staying safe on the road."
        theme="rose"
      />

      {/* Related Practice Areas */}
      <RelatedPracticeAreas currentSlug="motorcycle-crashes" />

      {/* CTA */}
      <section className="py-20 bg-teal-600 text-white">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
            Free Consultation - No Obligation
          </h2>
          <p className="text-xl text-teal-100 mb-8">
            I personally respond to every inquiry within 1 business day.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:425-675-1224"
              className="inline-flex items-center justify-center gap-2 bg-white text-teal-700 px-10 py-5 rounded-full font-bold text-xl hover:bg-teal-50 transition-all duration-300 shadow-xl"
              data-testid="motorcycle-cta-call"
            >
              <Phone className="w-6 h-6" />
              Call (425) 675-1224
            </a>
            <a 
              href="sms:425-675-1224"
              className="inline-flex items-center justify-center gap-2 border-2 border-white text-white px-10 py-5 rounded-full font-bold text-xl hover:bg-white/10 transition-all duration-300"
            >
              <MessageCircle className="w-6 h-6" />
              Text (425) 675-1224
            </a>
          </div>
          <p className="mt-6 text-teal-200 text-sm">
            Page last updated: December 2025
          </p>
        </div>
      </section>
    </div>
  );
};

export default MotorcyclePage;
